import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { supabase } from '../supabase';
import SEO from './SEO';
import logo from '../assets/pokeshani-logo.png';
import accessLinkImage from '../assets/accesslink.png';
import g1Image from '../assets/g1.png';
import g2Image from '../assets/g2.png';
import g3Image from '../assets/g3.png';
import './AccessUrlPage.css';

const AccessUrlPage = () => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const checkAuth = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      setIsAuthenticated(!!user);
    };
    checkAuth();
  }, []);

  const handleDashboardClick = () => {
    if (isAuthenticated) {
      navigate('/dashboard');
    } else {
      navigate('/login');
    }
  };

  return (
    <div className="access-url-container">
      <SEO 
        title="How Access URLs Work - PokeShani"
        description="Learn how access URLs work and how to create them using Google Drive and other hosting platforms"
        keywords="access URLs, Google Drive, file sharing, digital products, download links, hosting platforms"
        url="https://pokeshani.com/access-url"
      />
      
      <header className="access-url-header-section">
        <div className="access-url-header-content">
          <Link to="/" className="faq-logo-link">
            <img src={logo} alt="PokeShani Logo" className="access-url-logo" />
            <span className="access-url-site-name">PokeShani</span>
          </Link>
        </div>
        <button 
          onClick={handleDashboardClick}
          className="access-url-dashboard-btn"
        >
          Dashboard
        </button>
      </header>

      <div className="access-url-main-content">
        {/* Header */}
        <div className="access-url-header">
          <h1 className="access-url-title">How Access URLs Work</h1>
          <p className="access-url-subtitle">
            Learn how to create and share access URLs for your digital products using Google Drive and other hosting platforms
          </p>
        </div>

        {/* What are Access URLs */}
        <section className="access-url-section">
          <h2 className="access-url-section-title">What are Access URLs?</h2>
          <p className="text-gray-300 mb-4">
            Access URLs are direct links to your digital products that allow customers to download or access your content 
            after making a purchase. These URLs need to be publicly accessible so that our system can verify access and 
            deliver the content to your customers.
          </p>
          <div className="access-url-info-box">
            <p>
              <strong>Important:</strong> Access URLs must be publicly accessible without requiring login or authentication. 
              They should point directly to the file that customers will download or access.
            </p>
          </div>
        </section>

        {/* Google Drive Instructions */}
        <section className="access-url-section">
          <h2 className="access-url-section-title">Google Drive Instructions</h2>
          
          <div className="space-y-6">
            <div className="access-url-step">
              <div className="access-url-step-number">
                1
              </div>
              <div className="access-url-step-content">
                <h3>Upload your file to Google Drive</h3>
                <p>
                  Go to Google Drive and upload your digital product file. Make sure the file is in a format that your 
                  customers can access (PDF, ZIP, MP4, etc.).
                </p>
                <img 
                  src={g1Image} 
                  alt="Google Drive upload step 1" 
                  className="access-url-step-image"
                />
              </div>
            </div>

            <div className="access-url-step">
              <div className="access-url-step-number">
                2
              </div>
              <div className="access-url-step-content">
                <h3>Share the file and change permissions</h3>
                <p>
                  Right-click on your file and select "Share". In the sharing settings, change the permissions to 
                  "Anyone with the link can view". This is crucial for the access URL to work properly.
                </p>
                <img 
                  src={g2Image} 
                  alt="Google Drive share settings step 2" 
                  className="access-url-step-image"
                />
              </div>
            </div>

            <div className="access-url-step">
              <div className="access-url-step-number">
                3
              </div>
              <div className="access-url-step-content">
                <h3>Copy the sharing link</h3>
                <p>
                  Copy the sharing link provided by Google Drive. This is your access URL. Make sure the link works 
                  by testing it in an incognito window to ensure it's publicly accessible.
                </p>
                <img 
                  src={g3Image} 
                  alt="Google Drive copy link step 3" 
                  className="access-url-step-image"
                />
              </div>
            </div>

            <div className="access-url-step">
              <div className="access-url-step-number">
                4
              </div>
              <div className="access-url-step-content">
                <h3>Get the direct download link (optional)</h3>
                <p>
                  If you want a direct download link instead of a Google Drive preview page, you can convert the sharing 
                  link. Replace "open?id=" with "uc?id=" in the URL, or use the format: 
                  https://drive.google.com/uc?export=download&amp;id=FILE_ID
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Access Link Example */}
        <section className="access-url-section">
          <h2 className="access-url-section-title">Access Link Example</h2>
          <div>
            <img 
              src={accessLinkImage} 
              alt="Access link example" 
              className="access-url-step-image mx-auto"
            />
          </div>
          <p className="text-gray-300 mb-4">
            The above image shows an example of how an access link appears in your product settings. This link should 
            point directly to your downloadable content.
          </p>
        </section>

        {/* Other Hosting Platforms */}
        <section className="access-url-section">
          <h2 className="access-url-section-title">Other Hosting Platforms</h2>
          
          <div className="space-y-6">
            <div className="access-url-border-section">
              <h3 className="text-lg font-semibold mb-3">Dropbox</h3>
              <ul className="access-url-list">
                <li className="access-url-list-item">Upload your file to Dropbox</li>
                <li className="access-url-list-item">Right-click the file and select "Share"</li>
                <li className="access-url-list-item">Change sharing settings to "Anyone with the link can view"</li>
                <li className="access-url-list-item">Copy the sharing link as your access URL</li>
                <li className="access-url-list-item">For direct download, change dl=0 to dl=1 at the end of the URL</li>
              </ul>
            </div>

            <div className="access-url-border-section">
              <h3 className="text-lg font-semibold mb-3">OneDrive</h3>
              <ul className="access-url-list">
                <li className="access-url-list-item">Upload your file to OneDrive</li>
                <li className="access-url-list-item">Right-click the file and select "Share"</li>
                <li className="access-url-list-item">Change link settings to "Anyone with the link can access"</li>
                <li className="access-url-list-item">Copy the link and ensure it doesn't require login</li>
              </ul>
            </div>

            <div className="access-url-border-section">
              <h3 className="text-lg font-semibold mb-3">Direct File Hosting</h3>
              <ul className="access-url-list">
                <li className="access-url-list-item">Upload files to your own website or hosting service</li>
                <li className="access-url-list-item">Ensure the file is publicly accessible</li>
                <li className="access-url-list-item">Use the direct URL to the file as your access link</li>
                <li className="access-url-list-item">Make sure there are no authentication requirements</li>
              </ul>
            </div>
          </div>
        </section>

        {/* Best Practices */}
        <section className="access-url-section">
          <h2 className="access-url-section-title">Best Practices</h2>
          <ul className="access-url-list">
            <li className="access-url-list-item">Always test your access URL in an incognito/private browser window to ensure it's publicly accessible</li>
            <li className="access-url-list-item">Make sure the file size is reasonable for download speeds</li>
            <li className="access-url-list-item">Use descriptive file names that make sense to customers</li>
            <li className="access-url-list-item">Keep your files in a stable location to avoid broken links</li>
            <li className="access-url-list-item">Consider using a content delivery network (CDN) for better performance</li>
            <li className="access-url-list-item">Regularly check that your access URLs are still working</li>
          </ul>
        </section>

        {/* Troubleshooting */}
        <section className="access-url-section">
          <h2 className="access-url-section-title">Troubleshooting</h2>
          <div className="space-y-4">
            <div>
              <h3 className="font-semibold mb-2">Access URL not working?</h3>
              <ul className="access-url-list">
                <li className="access-url-list-item">Check if the file sharing permissions are set to "Anyone with link can view"</li>
                <li className="access-url-list-item">Verify the URL doesn't require login or authentication</li>
                <li className="access-url-list-item">Test the link in a browser where you're not logged into the hosting service</li>
                <li className="access-url-list-item">Ensure the file hasn't been moved or deleted</li>
              </ul>
            </div>
            <div>
              <h3 className="font-semibold mb-2">Customers can't access the file?</h3>
              <ul className="access-url-list">
                <li className="access-url-list-item">Confirm the file is still publicly accessible</li>
                <li className="access-url-list-item">Check if there are any regional restrictions</li>
                <li className="access-url-list-item">Verify the file format is supported by common devices</li>
              </ul>
            </div>
          </div>
        </section>
      </div>

      <footer className="access-url-footer">
        <div className="access-url-footer-content">
          <p>Need help? <a 
            href="https://x.com/PokeShani" 
            target="_blank" 
            rel="noopener noreferrer"
            className="access-url-support-link"
          >
            Contact our support team
          </a></p>
        </div>
      </footer>
    </div>
  );
};

export default AccessUrlPage;